/**
 * Where an emergency call LIVES.
 *
 * Mounted once in `AppLayout`, not on the page that started the call. That placement is the whole
 * reason this component exists: Accept is pressed on the emergency modal, which is mounted
 * per-dashboard, and an operator on a call needs to navigate — to the map to find the wearer, to
 * the roster, to a camera. A call that ended on navigation would be worse than no call at all.
 *
 * Portalled into a host that follows fullscreen. Mounted here, the panel is outside a fullscreen
 * dashboard's canvas, and the browser paints nothing outside it — the call carried on with Mute and
 * Hang up gone from the screen.
 */

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { useFullscreenHost } from '../../../hooks/useFullscreenHost'
import { endSosCall, subscribeSosCall, type SosCallTarget } from './sos-call-store'
import { SosCallPanel } from './SosCallPanel'

export function SosCallOverlay() {
  const [target, setTarget] = useState<SosCallTarget | null>(null)
  const host = useFullscreenHost()

  useEffect(() => subscribeSosCall(setTarget), [])

  if (!target || !host) return null

  return createPortal(
    // Keyed on the CALL, not the device.
    //
    // `robotId` was wrong in the case that matters most: the same wearer pressing SOS again. Once a
    // failed call stays on screen to be read, the next call to that helmet carries the same
    // `robotId`, so React reuses this panel instead of building a new one — its `startedRef` is
    // already latched, `start()` never runs, and no call is placed while the modal has just
    // promised one. `callId` changes on every call, so a fresh panel with a fresh hook is
    // guaranteed.
    <SosCallPanel
      key={target.callId}
      callId={target.callId}
      robotId={target.robotId}
      label={target.label}
      onClose={endSosCall}
    />,
    host,
  )
}
